/**
 * useWorldCupGroupsData — Adapter hook for the Squads tab.
 *
 * Fetches the authenticated user's squads from:
 *   GET /competitions/:competitionId/groups
 *
 * No client-side mock rows: signed-out and empty API responses show an empty list.
 */

import { useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSupabaseAuth } from '../../shared/contexts/SupabaseAuthContext';
import { useCompetitionId } from '../../competitions/contexts/CompetitionContext';
import { getWorldCupGroupCardEmoji } from '../helpers/worldCupGroupsDisplay.helper';
import { worldCupQueryKeys } from '../constants/worldcupQueryKeys';
import { useWorldCupGroupsQuery } from './useWorldCupGroupsQuery';
import type { WorldCupGroup, WorldCupGroupCompanyBranding } from '../types/worldcup.types';
import type { EquippedMap } from '../../../components/avatars/LayeredAvatarLab/characterCustomizer.types';

interface WorldCupGroupsData {
  groups: WorldCupGroup[];
  hasGroups: boolean;
  isLoading: boolean;
  isFetching: boolean;
  refetch: () => Promise<void>;
}

function mapApiGroup(g: {
  id: string;
  name: string;
  inviteCode?: string | null;
  description?: string | null;
  memberCount: number;
  userPoints: number;
  userRank: number;
  leadingUserName: string | null;
  memberPreview?: Array<{
    displayName: string;
    avatarUrl: string | null;
    avatarId: string | null;
    avatarConfig?: EquippedMap | null;
  }>;
  customLogoUrl: string | null;
  entryFeeCoins?: number;
  payoutMode?: string;
  payoutFirstPct?: number | null;
  payoutSecondPct?: number | null;
  payoutThirdPct?: number | null;
  companyBranding?: WorldCupGroupCompanyBranding | null;
}): WorldCupGroup {
  return {
    id: g.id,
    name: g.name,
    inviteCode: g.inviteCode ?? null,
    emoji: getWorldCupGroupCardEmoji(g.id),
    customLogoUrl: g.customLogoUrl,
    memberCount: g.memberCount,
    userRank: g.userRank,
    userPoints: g.userPoints,
    leadingUserName: g.leadingUserName ?? undefined,
    memberPreview: g.memberPreview?.map((row) => ({
      displayName: row.displayName,
      avatarUrl: row.avatarUrl,
      avatarId: row.avatarId,
      // The server has always sent this; dropping it here is why a custom look never once
      // reached a squad card or the info sheet, refresh or not.
      avatarConfig: row.avatarConfig ?? null,
    })),
    description: g.description,
    entryFeeCoins: g.entryFeeCoins,
    payoutMode: g.payoutMode,
    payoutFirstPct: g.payoutFirstPct,
    payoutSecondPct: g.payoutSecondPct,
    payoutThirdPct: g.payoutThirdPct,
    companyBranding: g.companyBranding ?? null,
  };
}

export function useWorldCupGroupsData(): WorldCupGroupsData {
  const queryClient = useQueryClient();
  const { isSignedIn, isLoaded } = useSupabaseAuth();
  const canFetch = isLoaded && isSignedIn;

  const competitionId = useCompetitionId();
  const groupsQuery = useWorldCupGroupsQuery(competitionId, canFetch);
  const refetchGroupsQuery = groupsQuery.refetch;

  const groups = useMemo(() => {
    if (!isLoaded || !isSignedIn) return [];
    return (groupsQuery.data ?? []).map(mapApiGroup);
  }, [groupsQuery.data, isLoaded, isSignedIn]);

  const refetch = useCallback(
    async () => {
      if (!canFetch) return;
      await queryClient.invalidateQueries({
        queryKey: worldCupQueryKeys.groups(competitionId),
      });
      await refetchGroupsQuery();
    },
    [canFetch, queryClient, refetchGroupsQuery],
  );

  const isLoading = !isLoaded || (isSignedIn && groupsQuery.isLoading);
  const isFetching = isSignedIn && groupsQuery.isFetching;

  return { groups, hasGroups: groups.length > 0, isLoading, isFetching, refetch };
}
